import {Component, inject, OnInit, signal, TemplateRef} from '@angular/core';
import {AccessService} from "../services/access.service";
import {DataService} from "../services/data.service";
import {ToastrService} from "ngx-toastr";
import moment from "moment";
import Swal from "sweetalert2";
import {DatePipe, DecimalPipe} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {TooltipDirective} from "ngx-bootstrap/tooltip";
import {InvoiceTypePipe} from "../pipes/invoice-type.pipe";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";

@Component({
    selector: 'app-invoice-franchise',
    imports: [DatePipe, DecimalPipe, FormsModule, TooltipDirective, InvoiceTypePipe],
    template: `
        <h5 class="mb-3">{{ 'franchise' | invoiceType }}</h5>

        <div class="d-flex align-items-center gap-3">
            <div class="input-group" style="width: 170px">
                <div class="input-group-prepend">
                    <button class="btn btn-outline-secondary" type="button" (click)="setYear(-1)">
                        <i class="fa-solid fa-arrow-left"></i>
                    </button>
                </div>
                <input type="number" class="form-control"
                       [(ngModel)]="year"
                       [max]="currentYear"
                       [disabled]="loading()"
                       (ngModelChange)="loadData()"/>
                <div class="input-group-append">
                    <button class="btn btn-outline-secondary" type="button" [disabled]="year == currentYear" (click)="setYear(1)">
                        <i class="fa-solid fa-arrow-right"></i>
                    </button>
                </div>
            </div>

            <div class="input-group" style="width: 200px">
                <div class="input-group-prepend">
                    <button class="btn btn-outline-secondary" type="button" (click)="setMonth(-1)">
                        <i class="fa-solid fa-arrow-left"></i>
                    </button>
                </div>
                <span class="input-group-text">{{ monthNames[month] }}</span>
                <div class="input-group-append">
                    <button class="btn btn-outline-secondary" type="button" [disabled]="year == currentYear && month == currentMonth - 1" (click)="setMonth(1)">
                        <i class="fa-solid fa-arrow-right"></i>
                    </button>
                </div>
            </div>
        </div>

        @if (stores?.length > 0) {
            <div class="table-responsive">
                <table class="table table-striped table-hover mt-3 mb-0 pointer">
                    <thead>
                    <tr>
                        <th style="width: 50px">
                            <i class="fa-solid fa-check"></i>
                        </th>
                        <th>
                            Store
                        </th>
                        <th class="text-end">
                            Belege
                        </th>
                        <th class="text-end">
                            Umsatz (€, netto)
                        </th>
                        <th class="text-end">
                            Franchisegebühr 8% (€)
                        </th>
                        <th class="text-end">
                            Sonderumsatz 6% (€)
                        </th>
                        <th class="text-end">
                            Werbegebühr (€)
                        </th>
                        <th class="text-end">
                            Gesamt (€)
                        </th>
                        <th>
                            Rechnung / Bericht
                        </th>
                        <th>
                            Rechnungsnummer
                        </th>
                        <th>
                            Versendet am
                        </th>
                        <th>
                            Bezahlt am
                        </th>
                    </tr>
                    <tr>
                        <th></th>
                        <th>
                            Summe
                        </th>
                        <th class="text-end">
                            {{ total.sales | number: '1.0' }}
                        </th>
                        <th class="text-end">
                            {{ total.turnover | number: '1.2-2' }}
                        </th>
                        <th class="text-end">
                            {{ total.franchise_costs | number: '1.2-2' }}
                        </th>
                        <th class="text-end">
                            {{ total.other | number: '1.2-2' }}
                        </th>
                        <th class="text-end">
                            {{ total.advertising_charge | number: '1.2-2' }}
                        </th>
                        <th class="text-end">
                            {{ total.sum | number: '1.2-2' }}
                        </th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                        @for (store of stores; track store.id) {
                            <tr>
                                <td>
                                    <input type="checkbox" class="form-check-input" [(ngModel)]="store.selected" (ngModelChange)="updateAnyStoreSelected()">
                                </td>
                                <td>{{ store.name }}</td>
                                <td class="text-end">
                                    {{ store.sales | number: '1.0' }}
                                    @if (store.sales == null) {
                                        <span class="text-muted">Keine Daten</span>
                                    }
                                </td>
                                <td class="text-end">
                                    {{ store.turnover | number: '1.2-2' }}
                                    @if (store.turnover == null) {
                                        <span class="text-muted">Keine Daten</span>
                                    }
                                </td>
                                <td class="text-end">
                                    {{ store.franchise_costs | number: '1.2-2' }}
                                </td>
                                <td class="text-end">
                                    {{ store.other | number: '1.2-2' }}
                                    @if (store.other == null) {
                                        <span class="text-muted">Keine Daten</span>
                                    }
                                </td>
                                <td class="text-end">
                                    {{ store.advertising_charge | number: '1.2-2' }}
                                </td>
                                <td class="text-end">
                                    {{ store.franchise_costs + store.other + store.advertising_charge | number: '1.2-2' }}
                                </td>
                                <td>
                                    @if (store.turnover != null) {
                                        <button type="button" class="btn btn-sm btn-outline-primary me-2" tooltip="Rechnung erstellen" [disabled]="loading()" (click)="createInvoice(store.id, pdfModal)">
                                            <i class="fa-solid fa-file-invoice"></i>
                                        </button>
                                        <button type="button" class="btn btn-sm btn-outline-primary" tooltip="Bericht erstellen" [disabled]="loading()" (click)="createReport(store.id)">
                                            <i class="fa-solid fa-store"></i>
                                        </button>
                                    }
                                </td>
                                <td>
                                    {{ store.invoice_number }}
                                    @if (store.invoice_number == null) {
                                        <span class="text-muted">Noch nicht erstellt</span>
                                    }
                                </td>
                                <td>
                                    {{ store.sent_at | date: 'dd.MM.yyyy' }}
                                    @if (store.sent_at == null) {
                                        <span class="text-muted">Noch nicht versendet</span>
                                    }
                                </td>
                                <td>
                                    {{ store.paid_at | date: 'dd.MM.yyyy' }}
                                    @if (store.paid_at == null) {
                                        <span class="text-muted me-2">Noch nicht bezahlt</span>
                                        <button type="button" class="btn btn-sm btn-outline-primary" tooltip="Datum erfassen" [disabled]="loading()" (click)="setPaidDate(store.id)">
                                            <i class="fa-solid fa-money-bill"></i>
                                        </button>
                                    }
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>

            <div class="d-flex gap-2 mt-3 mb-2">
                <button type="button" class="btn btn-sm btn-outline-primary" (click)="toggleAllOn()">
                    <i class="fa-solid fa-check-square me-1"></i>Alle auswählen
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="toggleAllOff()">
                    <i class="fa-solid fa-square me-1"></i>Alle abwählen
                </button>
            </div>

            @if (year < currentYear || (year == currentYear && month < currentMonth)) {
                <button type="button" class="btn btn-primary mt-3" [disabled]="!anyStoreSelected || loading()" (click)="sendInvoices()">
                    <i class="fa-solid fa-envelope"></i> Rechnung an ausgewählte Stores versenden
                </button>
            }
            <button type="button" class="btn btn-primary ms-3 mt-3" [disabled]="!anyStoreSelected || loading()" (click)="downloadInvoices()">
                <i class="fa-solid fa-download"></i> Ausgewählte Rechnungen herunterladen
            </button>

            <ng-template #pdfModal>
                <div class="modal-header">
                    <h4 class="modal-title pull-left">PDF Vorschau</h4>
                    <button type="button" class="btn-close close pull-right" aria-label="Close" (click)="modalRef?.hide()">
                        <span aria-hidden="true" class="visually-hidden">&times;</span>
                    </button>
                </div>
                <div class="modal-body" style="height: 80vh;">
                    @if (pdfUrl) {
                        <iframe [src]="pdfUrl" width="100%" height="100%" frameborder="0"></iframe>
                    }
                </div>
            </ng-template>
        }
    `
})
export class InvoiceFranchiseComponent implements OnInit {

    year: any;
    currentYear!: number;
    currentMonth!: number;
    month: any;
    stores!: any[];
    total: any;
    anyStoreSelected: boolean = false;

    monthNames = ['Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

    accessService = inject(AccessService);
    private dataService = inject(DataService);
    private toastrService = inject(ToastrService);
    private modalService = inject(BsModalService);
    private sanitizer = inject(DomSanitizer);

    modalRef?: BsModalRef;
    pdfUrl: SafeResourceUrl | null = null;
    loading = signal(false);

    ngOnInit(): void {
        this.currentYear = moment().year();
        this.currentMonth = moment().month();

        const date = moment().subtract(1, 'months');
        this.year = date.year();
        this.month = date.month();

        this.loadData();
    }

    setYear(number: number) {
        this.year += number;

        if (this.year == this.currentYear && this.month >= this.currentMonth) {
            this.month = this.currentMonth;
        }

        this.loadData();
    }

    setMonth(number: number) {
        this.month += number;
        if (this.month < 0) this.month = 0;
        if (this.month > 11) this.month = 11;

        this.loadData();
    }

    loadData() {
        this.dataService.request("Invoice/getForFranchiseType", {
            year: this.year,
            month: this.month,
        }).subscribe(response => {
            this.stores = response.stores;

            this.total = {
                sales: 0,
                turnover: 0,
                franchise_costs: 0,
                other: 0,
                advertising_charge: 0,
                sum: 0,
            }
            this.stores.forEach(store => {
                store.selected = false;

                store.turnover = parseFloat(store.turnover);
                store.turnover_delivery = parseFloat(store.turnover_delivery);
                store.sales = parseInt(store.sales);

                store.franchise_costs = store.turnover * store.franchise_charge / 100; // + store.turnover_delivery * 6 / 100;
                store.other = store.turnover_delivery * 6 / 100;

                this.total.sales += store.sales;
                this.total.turnover += store.turnover;
                this.total.franchise_costs += store.franchise_costs;
                this.total.other += store.other;
                this.total.advertising_charge += store.advertising_charge;
                this.total.sum += store.franchise_costs + store.other + store.advertising_charge;
            });
        })
    }

    createInvoice(id_store: any, template: TemplateRef<any>) {
        this.loading.set(true);
        this.dataService.request("Invoice/createInvoiceFranchiseType", {
            id_store: id_store,
            year: this.year,
            month: this.month,
        }).subscribe(response => {
            this.loading.set(false);

            this.stores.find(store => store.id == id_store).invoice_number = response.invoiceNumber;

            if (response?.pdf) {
                const byteCharacters = atob(response.pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {type: 'application/pdf'});
                const url = URL.createObjectURL(blob);

                this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                const currentModalRef = this.modalService.show(template, {class: 'modal-xl'});
                this.modalRef = currentModalRef;

                const subscription = this.modalService.onHidden.subscribe((modalRef: any) => {
                    if (modalRef === currentModalRef) {
                        this.pdfUrl = null;
                        URL.revokeObjectURL(url);
                        subscription.unsubscribe();
                    }
                });
            }
        });
    }

    toggleAllOn() {
        this.stores.forEach(store => store.selected = true);
        this.anyStoreSelected = true;
    }

    toggleAllOff() {
        this.stores.forEach(store => store.selected = false);
        this.anyStoreSelected = false;
    }

    updateAnyStoreSelected() {
        this.anyStoreSelected = this.stores.some(store => store.selected);
    }

    sendInvoices() {
        const selectedStores = this.stores.filter(store => store.selected);
        const storeCount = selectedStores.length;

        Swal.fire({
            title: 'Rechnungen versenden?',
            text: `Die Rechnungen werden an ${storeCount} ausgewählte Stores versandt.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Versenden',
            cancelButtonText: 'Abbrechen',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
        }).then((result) => {
            if (result.isConfirmed) {
                this.loading.set(true);
                this.dataService.request("Invoice/sendInvoicesFranchiseType", {
                    id_stores: selectedStores.map(store => store.id),
                    year: this.year,
                    month: this.month,
                }).subscribe(response => {
                    this.loading.set(false);

                    if (response?.error) {
                        alert('Error: ' + response.error);
                        return;
                    }

                    this.loadData();

                    this.toastrService.success("Die Rechnungen wurden versandt!");
                });
            }
        });
    }

    downloadInvoices() {
        this.dataService.request("Invoice/downloadInvoicesFranchiseType", {
            id_stores: this.stores.filter(store => store.selected).map(store => store.id),
            year: this.year,
            month: this.month,
        }).subscribe(response => {
            if (response?.error) {
                alert('Error: ' + response.error);
                return;
            }

            if (response?.zip && response?.filename) {
                const byteCharacters = atob(response.zip);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {type: 'application/zip'});
                const url = URL.createObjectURL(blob);

                // Create a temporary download link and trigger it
                const a = document.createElement('a');
                a.href = url;
                a.download = response.filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);

                // Revoke the URL after a short delay
                setTimeout(() => URL.revokeObjectURL(url), 100);
            }

            this.loadData();
        });
    }

    createReport(id_store: any) {
        this.dataService.request("Data/createMonthlyStoreReport", {
            id_store: id_store,
            year: this.year,
            month: this.month,
        }).subscribe(response => {
            if (response?.pdf) {
                const byteCharacters = atob(response.pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {type: 'application/pdf'});
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                // Optionally revoke after some delay
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            }
        });
    }

    protected setPaidDate(id: any) {
        Swal.fire({
            title: 'Bezahlt am',
            input: 'date',
            inputValue: moment().format('YYYY-MM-DD'),
            showCancelButton: true,
            confirmButtonText: 'Speichern',
            cancelButtonText: 'Abbrechen',
            confirmButtonColor: this.accessService.client.primaryColor,
            cancelButtonColor: '#d33'
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                this.loading.set(true);
                this.dataService.request("Invoice/setPaidDateFranchise", {
                    id_store: id,
                    id_invoice_type: 1, // Franchise
                    year: this.year,
                    month: this.month,
                    paid_at: result.value
                }).subscribe(response => {
                    this.loading.set(false);
                    this.toastrService.success("Datum wurde gespeichert!");
                    this.loadData();
                });
            }
        });
    }
}
